import React, { useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import { addToCart, MAX_QUANTITY } from "../../../Features/Cart/cartSlice";
import { RWebShare } from "react-web-share";
import { GoChevronDown, GoChevronLeft, GoChevronRight } from "react-icons/go";
import { FaStar } from "react-icons/fa";
import { FiHeart } from "react-icons/fi";
import { PiShareNetworkLight } from "react-icons/pi";

import "./Product.css";
import {
  getCategoryList,
  getSingleProductsList,
  toastError,
  toastSuccess,
} from "../../../api/services";
import RelatedProducts from "../RelatedProducts/RelatedProducts";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from "@mui/material";
import Skeleton from "react-loading-skeleton";

const Product = ({ slug }) => {
  const [loading, setLoading] = useState(true);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingProductId, setLoadingProductId] = useState(null);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const res = await getCategoryList();
        setCategories(res?.data?.data || []);
      } catch (err) {
        console.error("Category error:", err);
      }
    };

    loadCategories();
  }, []);

  useEffect(() => {
    if (!categories.length) return;

    const fetchProducts = async () => {
      setLoading(true);
      try {
        const res = await getSingleProductsList(slug);
        const data = res?.data?.data;
        setProducts(data || []);
        if (data?.product_variants?.length > 0) {
          setSelectedVariant(data.product_variants[0]);
        }
      } catch (err) {
        console.error("Product error:", err);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [categories.length, slug]);

  const images = selectedVariant?.images || [products.image];
  const [currentImg, setCurrentImg] = useState(0);

  const prevImg = () => {
    setCurrentImg(currentImg === 0 ? images.length - 1 : currentImg - 1);
  };

  const nextImg = () => {
    setCurrentImg(currentImg === images.length - 1 ? 0 : currentImg + 1);
  };

  const [quantity, setQuantity] = useState(1);

  const increment = () => {
    setQuantity(quantity + 1);
  };

  const decrement = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const handleInputChange = (event) => {
    const value = Number.parseInt(event.target.value);
    if (!Number.isNaN(value) && value > 0) {
      setQuantity(value);
    }
  };

  const [clicked, setClicked] = useState(false);

  const handleWishClick = () => {
    setClicked(!clicked);
  };

  const dispatch = useDispatch();

  const cartItems = useSelector((state) => state.cart.items);

  const handleAddToCart = async (data) => {
    if (!selectedVariant) {
      toastError("Please select a variant");
      return;
    }

    if (quantity < 1) {
      toastError("Quantity must be at least 1");
      return;
    }

    if (quantity > selectedVariant.stock) {
      toastError(`Only ${selectedVariant.stock} items available in stock`);
      return;
    }

    const minDelay = new Promise((resolve) => setTimeout(resolve, 500));
    setLoadingProductId(data.id);

    try {
      const productDetails = {
        productID: data.id,
        variantID: selectedVariant.id,
        productName: `${data.name} (${selectedVariant.size || selectedVariant.weight || "Default"})`,
        productPrice: selectedVariant.final_price || selectedVariant.price,
        price: selectedVariant.price,
        discount: selectedVariant.discount || 0,
        discount_amount: selectedVariant.discount_amount || 0,
        frontImg: selectedVariant.images?.[0] || data.image,
        productReviews: data.reviews || "8k+ reviews",
        quantity: quantity,
      };

      // Check if already in cart
      const existingItem = cartItems.find(
        (item) =>
          item.productID === data.id && item.variantID === selectedVariant.id,
      );

      const currentQty = existingItem ? existingItem.quantity : 0;
      const newTotal = currentQty + quantity;

      if (newTotal > MAX_QUANTITY) {
        toastError(`You can only add up to ${MAX_QUANTITY} of this variant`);
        return;
      }

      dispatch(addToCart(productDetails));
      toastSuccess(
        `${quantity} x ${productDetails.productName} added to cart!`,
      );
    } catch (error) {
      console.error("Add to cart error:", error);
      toastError("Failed to add to cart. Please try again.");
    } finally {
      await minDelay;
      setLoadingProductId(null);
    }
  };
 
  const renderPrice = (original, discountPercent) => {
    const final = original * (1 - discountPercent / 100);
    return (
      <div className="product_price_row d-flex">
        <ins>
          <i className="fas fa-rupee-sign"></i>
          {final.toFixed(2)}
        </ins>
        {discountPercent > 0 && (
          <del>
            <i className="fas fa-rupee-sign"></i>
            {original.toFixed(2)}
          </del>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="container" style={{ marginTop: "20px" }}>
        <div className="productShowCase">
          <div className="productGallery">
            <div className="productThumb">
              {[...Array(4)].map((_, i) => (
                <Skeleton
                  key={i.toLocaleString()}
                  className="react-loading-skeleton-small"
                  style={{ marginBottom: 10 }}
                />
              ))}
            </div>
            <div className="productFullImg">
              <Skeleton className="react-loading-skeleton-food" />
            </div>
          </div>
          <div className="productDetails">
            <div
              style={{
                width: "100%",
                borderRadius: 18,
              }}
            >
              <Skeleton height={30} width="100%" />
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  borderRadius: 18,
                }}
              >
                <Skeleton height={30} width="30%" />
              </div>
            </div>
            <div className="amountReview">
              <div className="productRating">
                {[...Array(5)].map((_, i) => (
                  <div key={i.toLocaleString()}>
                    <Skeleton height={10} width={10} />
                  </div>
                ))}
                <p>
                  <Skeleton height={10} width={80} />
                </p>
              </div>
              <div className="productPrice">
                <Skeleton height={10} width="20%" />
              </div>
            </div>
            <div className="productSizeColorScan">
              <Skeleton height={10} width="100%" />

              <div className="grid">
                {[...Array(4)].map((_, i) => (
                  <span key={i.toLocaleString()}>
                    <Skeleton height={40} width="100%" />
                  </span>
                ))}
              </div>

              <div className="actions">
                <div className="productCartBtn">
                  <Skeleton height={25} width={200} />
                </div>
              </div>
            </div>

            <div className="productTags">
              <p>
                <Skeleton height={20} width={300} />
              </p>
              <p>
                <span>
                  <Skeleton height={50} width="100%" />
                </span>
              </p>
            </div>
          </div>
        </div>
        <div style={{ marginTop: "14px" }}>
          <Skeleton height={200} width="100%" />
        </div>
        <div
          style={{
            width: "100%",
            margin: "20px auto",
            background: "#fff",
            borderRadius: 18,
            padding: "18px 22px",
          }}
        >
          {[...Array(4)].map((_, i) => (
            <div key={i.toLocaleString()} style={{ marginBottom: 20 }}>
              <Skeleton height={30} width="30%" />
              <Skeleton height={60} style={{ marginTop: 10 }} />
            </div>
          ))}
        </div>
      </div>
    );
  } else {
    return (
      <div className="container" style={{ marginTop: "20px" }}>
        <div className="productShowCase">
          <div className="productGallery">
            <div className="productThumb">
              {images.map((img, i) => (
                <div key={i.toLocaleString()}>
                  <img
                    src={img}
                    onClick={() => setCurrentImg(i)}
                    alt="product thumbnail"
                  />
                </div>
              ))}
            </div>
            <div className="productFullImg">
              <img src={images[currentImg]} alt="" />
              <div className="buttonsGroup">
                <button onClick={prevImg} className="directionBtn">
                  <GoChevronLeft size={18} />
                </button>
                <button onClick={nextImg} className="directionBtn">
                  <GoChevronRight size={18} />
                </button>
              </div>
            </div>
          </div>

          <div className="productDetails">
            <div className="productName">
              <h1>{products.name}</h1>
            </div>
            <div className="amountReview">
              <div className="productRating">
                {[...Array(5)].map((_, i) => (
                  <FaStar key={i.toLocaleString()} color="#FEC78A" size={10} />
                ))}
                <p>8k+ reviews</p>
              </div>
              <div className="productPrice">
                {renderPrice(selectedVariant?.price, selectedVariant?.discount)}
                <p>Inclusive of all taxes</p>
              </div>
            </div>
            <div className="productSizeColor">
              <h2>
                Size:{" "}
                <span>
                  {selectedVariant
                    ? `${selectedVariant.size_value} ${selectedVariant.size_unit}`
                    : ""}
                </span>
              </h2>

              <div className="grid">
                {products?.product_variants?.map((item, index) => (
                  <div
                    key={item.id.toLocaleString()}
                    className={`card ${
                      selectedVariant?.id === item.id ? "active" : ""
                    }`}
                    onClick={() => {
                      setSelectedVariant(item);
                      setCurrentImg(0);
                    }}
                  >
                    <span>
                      {item.size_value} {item.size_unit}
                    </span>
                    <strong>Rs. {item.final_price.toLocaleString()}</strong>
                  </div>
                ))}
              </div>

              <div className="actions">
                <div className="productQuantity">
                  <button onClick={decrement}>-</button>
                  <input
                    type="text"
                    value={quantity}
                    onChange={handleInputChange}
                  />
                  <button onClick={increment}>+</button>
                </div>
                <div className="productCartBtn">
                  <button
                    onClick={() => {
                      handleAddToCart(products);
                    }}
                  >
                    {loadingProductId === products.id ? (
                      <span className="spinner"></span>
                    ) : (
                      "Add to Cart"
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className="productWishShare">
              <div className="productWishList">
                <button onClick={handleWishClick}>
                  <FiHeart color={clicked ? "red" : ""} size={17} />
                  <p>Add to Wishlist</p>
                </button>
              </div>
              <div className="productShare">
                <RWebShare
                  data={{
                    text: products.description,
                    url: products.image,
                    title: products.name,
                  }}
                  onClick={() => console.log("shared successfully!")}
                >
                  <button className="productBtnShare">
                    {" "}
                    <PiShareNetworkLight size={22} />
                    <p>Share</p>
                  </button>
                </RWebShare>
              </div>
            </div>
            <div className="productTags">
              <p>
                {selectedVariant ? (
                  <span>
                    SKU:{" "}
                    <span style={{ color: "#000000" }}>
                      {selectedVariant.sku}
                    </span>
                  </span>
                ) : null}
              </p>
              <p>
                {selectedVariant ? (
                  <span>
                    CATEGORIES:{" "}
                    <span style={{ color: "#000000" }}>
                      {products.category_name}
                    </span>
                  </span>
                ) : null}
              </p>
            </div>
          </div>
        </div>

        <div
          style={{
            width: "100%",
            margin: "50px auto",
            background: "#ffffff",
            borderRadius: "18px",
            padding: "18px 22px",
          }}
        >
          {/* Accordion 1 - Ingredients */}
          <Accordion
            elevation={0}
            sx={{
              boxShadow: "none",
              paddingBottom: "15px",
              borderBottom: "1px solid #ddd",
              "&:before": { display: "none" },
              margin: 0,
              "&.Mui-expanded": { margin: 0 },
            }}
          >
            <AccordionSummary
              expandIcon={<GoChevronDown />}
              sx={{
                fontSize: "20px",
                fontWeight: 500,
                color: "#111111",
                "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                  transform: "rotate(180deg)",
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: "20px",
                  fontWeight: 500,
                  color: "#111111",
                }}
              >
                Ingredients
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: "8px 16px" }}>
              <Box
                sx={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "15px",
                  color: "#333",
                  letterSpacing: "0.6px",
                }}
                dangerouslySetInnerHTML={{ __html: products?.ingredients }}
              />
            </AccordionDetails>
          </Accordion>

          {/* Accordion 2 - Benefits */}
          <Accordion
            elevation={0}
            sx={{
              boxShadow: "none",
              paddingBottom: "15px",
              borderBottom: "1px solid #ddd",
              "&:before": { display: "none" },
              margin: 0,
              "&.Mui-expanded": { margin: 0 },
            }}
          >
            <AccordionSummary
              expandIcon={<GoChevronDown />}
              sx={{
                fontSize: "20px",
                fontWeight: 500,
                color: "#111111",
                "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                  transform: "rotate(180deg)",
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: "20px",
                  fontWeight: 500,
                  color: "#111111",
                }}
              >
                Benefits
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: "8px 16px" }}>
              <Box
                sx={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "15px",
                  color: "#333",
                  letterSpacing: "0.6px",
                }}
                dangerouslySetInnerHTML={{ __html: products?.benefits }}
              />
            </AccordionDetails>
          </Accordion>

          {/* Accordion 3 - Storage Info */}
          <Accordion
            elevation={0}
            sx={{
              boxShadow: "none",
              paddingBottom: "15px",
              borderBottom: "1px solid #ddd",
              "&:before": { display: "none" },
              margin: 0,
              "&.Mui-expanded": { margin: 0 },
            }}
          >
            <AccordionSummary
              expandIcon={<GoChevronDown />}
              sx={{
                fontSize: "20px",
                fontWeight: 500,
                color: "#111111",
                "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                  transform: "rotate(180deg)",
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: "20px",
                  fontWeight: 500,
                  color: "#111111",
                }}
              >
                Storage Info
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: "8px 16px" }}>
              <Box
                sx={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "15px",
                  color: "#333",
                  letterSpacing: "0.6px",
                }}
                dangerouslySetInnerHTML={{ __html: products?.storage_info }}
              />
            </AccordionDetails>
          </Accordion>

          {/* Accordion 4 - Shelf life */}
          <Accordion
            elevation={0}
            sx={{
              boxShadow: "none",
              paddingBottom: "15px",
              borderBottom: "1px solid #ddd",
              "&:before": { display: "none" },
              margin: 0,
              "&.Mui-expanded": { margin: 0 },
            }}
          >
            <AccordionSummary
              expandIcon={<GoChevronDown />}
              sx={{
                fontSize: "20px",
                fontWeight: 500,
                color: "#111111",
                "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                  transform: "rotate(180deg)",
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: "20px",
                  fontWeight: 500,
                  color: "#111111",
                }}
              >
                Shelf life
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: "8px 16px" }}>
              <Box
                sx={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "15px",
                  color: "#333",
                  letterSpacing: "0.6px",
                }}
                dangerouslySetInnerHTML={{ __html: products?.shelf_life }}
              />
            </AccordionDetails>
          </Accordion>
        </div>

        {products && products.category_id ? (
          <RelatedProducts catId={products.category_id} />
        ) : null}
      </div>
    );
  }
};

export default Product;
